import React, { useEffect, useMemo, useState } from 'react';
import { CloseOutlined, ProfileOutlined, ShoppingCartOutlined } from '@ant-design/icons';

import type { ManualDiscountDraft, PosSaleLine } from '../types';
import type { PosPermissions } from '../hooks/usePosPermissions';
import './ManualDiscountModal.scss';
import {
  validateManualDiscount,
  type DiscountScope,
} from '../utils/discountPermissions';

interface ManualDiscountModalProps {
  isOpen: boolean;
  lines: PosSaleLine[];
  initialLineId: number | null;
  initialScope?: DiscountScope;
  orderDiscount?: ManualDiscountDraft | null;
  onClose: () => void;
  onApplyLine: (lineId: number, discount: ManualDiscountDraft | null) => void;
  onApplyOrder: (discount: ManualDiscountDraft | null) => void;
  canApplyLineDiscount?: boolean;
  canApplyOrderDiscount?: boolean;
  permissions?: PosPermissions;
}

export const ManualDiscountModal: React.FC<ManualDiscountModalProps> = ({
  isOpen,
  lines,
  initialLineId,
  initialScope,
  orderDiscount,
  onClose,
  onApplyLine,
  onApplyOrder,
  canApplyLineDiscount = true,
  canApplyOrderDiscount = true,
  permissions,
}) => {
  const hasLines = lines.length > 0;
  const allowLineScope = canApplyLineDiscount && hasLines;
  const allowOrderScope = canApplyOrderDiscount;
  const defaultScope: DiscountScope = initialScope && (initialScope === 'line' ? allowLineScope : allowOrderScope)
    ? initialScope
    : allowLineScope && !allowOrderScope
      ? 'line'
      : 'order';
  const [scope, setScope] = useState<DiscountScope>(defaultScope);
  const [selectedLineId, setSelectedLineId] = useState<number | null>(initialLineId ?? lines[0]?.item.id ?? null);
  const [mode, setMode] = useState<ManualDiscountDraft['mode']>('percentage');
  const [valueInput, setValueInput] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const fallbackPermissions: PosPermissions = permissions ?? {
    isSuperuser: false,
    canApplyLineDiscount,
    canApplyOrderDiscount,
    canOverrideCommission: false,
    canEditPrice: false,
    canViewRestrictedCategories: false,
  };

  const selectedLine = useMemo(
    () => lines.find((line) => line.item.id === selectedLineId) ?? lines[0] ?? null,
    [lines, selectedLineId],
  );

  const activeDiscount = useMemo(() => {
    if (scope === 'line') {
      return selectedLine?.manualDiscount ?? null;
    }
    return orderDiscount ?? null;
  }, [orderDiscount, scope, selectedLine?.manualDiscount]);

  const orderSubtotal = useMemo(
    () => lines.reduce((total, line) => total + (line.item.price?.value ?? 0) * line.quantity, 0),
    [lines],
  );

  const activeSubtotal = useMemo(() => {
    if (scope === 'line' && selectedLine) {
      return (selectedLine.item.price?.value ?? 0) * selectedLine.quantity;
    }
    return orderSubtotal;
  }, [orderSubtotal, scope, selectedLine]);

  useEffect(() => {
    if (!isOpen) return;
    const nextScope: DiscountScope = initialScope && (initialScope === 'line' ? allowLineScope : allowOrderScope)
      ? initialScope
      : allowLineScope && !allowOrderScope
        ? 'line'
        : 'order';
    setScope(nextScope);
    setSelectedLineId(initialLineId ?? lines[0]?.item.id ?? null);
  }, [allowLineScope, allowOrderScope, initialLineId, initialScope, isOpen, lines]);

  useEffect(() => {
    if (!isOpen) return;
    if (activeDiscount) {
      setMode(activeDiscount.mode);
      setValueInput(activeDiscount.mode === 'percentage' ? String(activeDiscount.value * 100) : String(activeDiscount.value));
      setReason(activeDiscount.reason);
      setNote(activeDiscount.note ?? '');
    } else {
      setMode('percentage');
      setValueInput('');
      setReason('');
      setNote('');
    }
    setError(null);
  }, [activeDiscount, isOpen]);

  if (!isOpen) return null;

  if (!allowOrderScope && !allowLineScope) {
    return (
      <div className="manual-discount-overlay" role="presentation" onClick={onClose}>
        <div className="manual-discount-modal" role="dialog" aria-modal="true" aria-labelledby="manual-discount-title" onClick={(event) => event.stopPropagation()}>
          <div className="manual-discount-modal__header">
            <h2 id="manual-discount-title">Descuento manual</h2>
            <button type="button" className="manual-discount-modal__close" onClick={onClose} aria-label="Cerrar">
              <CloseOutlined />
            </button>
          </div>
          <p className="manual-discount-modal__error-copy">
            No tiene permisos para aplicar descuentos manuales.
          </p>
          <div className="manual-discount-modal__footer">
            <button type="button" onClick={onClose} className="manual-discount-modal__button manual-discount-modal__button--secondary">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = Number(valueInput.replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('El valor debe ser mayor a cero.');
      return;
    }

    let normalizedValue = parsed;
    if (mode === 'percentage') {
      if (parsed > 100) {
        setError('El porcentaje no puede superar 100%.');
        return;
      }
      normalizedValue = parsed / 100;
    }

    const discount: ManualDiscountDraft = {
      mode,
      value: normalizedValue,
      reason: reason.trim() || 'Descuento aplicado',
      note: note.trim() ? note.trim() : undefined,
    };

    const validation = validateManualDiscount({
      draft: discount,
      permissions: fallbackPermissions,
      scope,
      subtotal: activeSubtotal,
    });

    if (!validation.isAllowed) {
      setError(validation.reason ?? 'El descuento excede el limite permitido.');
      return;
    }

    if (scope === 'line') {
      if (selectedLineId == null) {
        setError('Seleccione una linea para aplicar el descuento.');
        return;
      }
      onApplyLine(selectedLineId, discount);
      return;
    }
    onApplyOrder(discount);
  };

  const handleRemoveDiscount = () => {
    if (scope === 'line') {
      if (selectedLineId != null) {
        onApplyLine(selectedLineId, null);
      }
      return;
    }
    onApplyOrder(null);
  };

  const hasRemovableDiscount = Boolean(activeDiscount);

  return (
    <div className="manual-discount-overlay" role="presentation" onClick={onClose}>
      <div className="manual-discount-modal" role="dialog" aria-modal="true" aria-labelledby="manual-discount-title" onClick={(event) => event.stopPropagation()}>
        <div className="manual-discount-modal__header">
          <div>
            <h2 id="manual-discount-title">Descuento manual (F6)</h2>
            <p>Aplica un descuento a la orden o a la línea seleccionada.</p>
          </div>
          <button type="button" className="manual-discount-modal__close" onClick={onClose} aria-label="Cerrar">
            <CloseOutlined />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="manual-discount-modal__form">
          {allowLineScope && allowOrderScope ? (
            <section className="manual-discount-modal__section">
              <span className="manual-discount-modal__step-label"><b>1.</b> Aplicar descuento a</span>
              <div className="manual-discount-modal__choice-grid">
              <label className={`manual-discount-modal__choice ${scope === 'order' ? 'is-active' : ''}`}>
                <input className="manual-discount-modal__visually-hidden" type="radio" name="discount-scope" value="order" checked={scope === 'order'} onChange={() => setScope('order')} />
                <ShoppingCartOutlined /> <span>Orden</span>
              </label>
              <label className={`manual-discount-modal__choice ${scope === 'line' ? 'is-active' : ''}`}>
                <input className="manual-discount-modal__visually-hidden" type="radio" name="discount-scope" value="line" checked={scope === 'line'} onChange={() => setScope('line')} />
                <ProfileOutlined /> <span>Linea</span>
              </label>
              </div>
            </section>
          ) : null}

          {scope === 'line' && allowLineScope ? (
            <label className="manual-discount-modal__field">
              <span className="manual-discount-modal__field-label">Linea</span>
              <select value={selectedLineId ?? ''} onChange={(event) => setSelectedLineId(Number(event.target.value))}>
                {lines.map((line) => (
                  <option key={line.item.id} value={line.item.id}>{line.item.name}</option>
                ))}
              </select>
            </label>
          ) : null}

          <div className="manual-discount-modal__details-grid">
            <section className="manual-discount-modal__section">
              <span className="manual-discount-modal__step-label"><b>2.</b> Tipo de descuento</span>
              <div className="manual-discount-modal__segmented-control">
                <label className={mode === 'percentage' ? 'is-active' : ''}>
                  <input className="manual-discount-modal__visually-hidden" type="radio" name="discount-mode" value="percentage" checked={mode === 'percentage'} onChange={() => setMode('percentage')} />%
                </label>
                <label className={mode === 'amount' ? 'is-active' : ''}>
                  <input className="manual-discount-modal__visually-hidden" type="radio" name="discount-mode" value="amount" checked={mode === 'amount'} onChange={() => setMode('amount')} />$
                </label>
              </div>
            </section>

            <label className="manual-discount-modal__field">
              <span className="manual-discount-modal__step-label"><b>3.</b> Valor del descuento</span>
              <div className="manual-discount-modal__value-input">
                <input type="number" inputMode="decimal" min={0} step={mode === 'percentage' ? 0.1 : 0.01} value={valueInput} onChange={(event) => setValueInput(event.target.value)} required placeholder={mode === 'percentage' ? '10' : '500'} />
                <span>{mode === 'percentage' ? '%' : '$'}</span>
              </div>
            </label>
            </div>

          <label className="manual-discount-modal__field">
            <span className="manual-discount-modal__step-label"><b>4.</b> Motivo</span>
            <input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Ej: Promo cliente" />
          </label>

          <label className="manual-discount-modal__field">
            <span className="manual-discount-modal__field-label">Nota <em>(opcional)</em></span>
            <textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder="Nota interna" rows={2} />
          </label>

          {error ? (
            <div role="alert" className="manual-discount-modal__error">
              {error}
            </div>
          ) : null}

          <div className="manual-discount-modal__footer">
            {hasRemovableDiscount ? (
              <button type="button" onClick={handleRemoveDiscount} className="manual-discount-modal__button manual-discount-modal__button--danger">
                Quitar
              </button>
            ) : null}
            <button type="button" onClick={onClose} className="manual-discount-modal__button manual-discount-modal__button--secondary">
              Cancelar
            </button>
            <button type="submit" className="manual-discount-modal__button manual-discount-modal__button--primary">
              Aplicar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualDiscountModal;
